import React from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, Building2, Shield, Award, CheckCircle2, Users } from 'lucide-react';
import AnimatedCounter from '../components/AnimatedCounter';
import SectorTiles from '../components/SectorTiles';
import ParallaxSection from '../components/ParallaxSection';
import ExpandableFramework from '../components/ExpandableFramework';
import MagneticButton from '../components/MagneticButton';
import SplitScreenImages from '../components/SplitScreenImages';
import FloatingImageGallery from '../components/FloatingImageGallery';
import ImageRevealOnScroll from '../components/ImageRevealOnScroll';
import { companyInfo, sectors, processSteps, whyApexReasons, deliveryFramework } from '../data/mock';

const FlagshipHomePage = () => {
  const navigate = useNavigate();

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
        delayChildren: 0.3
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.8,
        ease: [0.6, 0.05, 0.01, 0.9]
      }
    }
  };

  return (
    <div className="min-h-screen">
      {/* Cinematic Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
        {/* Animated Background with Gradient Overlay */}
        <div className="absolute inset-0 z-0">
          {/* Dynamic Gradient Overlay */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 2 }}
            className="absolute inset-0 bg-gradient-to-br from-navy-dark via-navy-dark/98 to-navy-darker/95 z-10"
          />
          
          {/* Background Image with Ken Burns Effect */}
          <motion.img
            initial={{ scale: 1.2, opacity: 0 }}
            animate={{ scale: 1, opacity: 0.25 }}
            transition={{ duration: 3, ease: 'easeOut' }}
            src="https://images.unsplash.com/photo-1717750670931-ba1a4b870827"
            alt="Infrastructure"
            className="w-full h-full object-cover"
          />
          
          {/* Subtle Moving Light */}
          <motion.div
            animate={{
              x: ['0%', '100%', '0%'],
              y: ['0%', '50%', '0%']
            }}
            transition={{
              duration: 20,
              repeat: Infinity,
              ease: 'linear'
            }}
            className="absolute w-96 h-96 bg-white opacity-5 rounded-full blur-3xl"
            style={{ top: '20%', left: '-10%' }}
          />
        </div>

        {/* Hero Content with Animated Text Reveal */}
        <div className="relative z-20 max-w-7xl mx-auto px-8 py-32 text-center">
          <motion.div
            variants={containerVariants}
            initial="hidden"
            animate="visible"
            className="max-w-5xl mx-auto"
          >
            {/* Label */}
            <motion.div variants={itemVariants} className="flex items-center justify-center gap-3 mb-12">
              <Building2 className="w-8 h-8 text-white" strokeWidth={1.5} />
              <span className="text-sm text-gray-300 tracking-[0.3em] uppercase font-semibold">
                Independent Project Leadership
              </span>
            </motion.div>
            
            {/* Headline with Character-by-Character Reveal */}
            <motion.h1
              variants={itemVariants}
              className="text-7xl md:text-[8rem] font-bold leading-[1.1] mb-12 tracking-tighter text-white"
            >
              <motion.span
                initial={{ opacity: 0, y: 50 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.5 }}
                className="block"
              >
                Strategic leadership.
              </motion.span>
              <motion.span
                initial={{ opacity: 0, y: 50 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.7 }}
                className="block"
              >
                Confident delivery.
              </motion.span>
            </motion.h1>
            
            {/* Subheading */}
            <motion.p
              variants={itemVariants}
              className="text-2xl md:text-3xl text-gray-200 leading-relaxed mb-16 max-w-4xl mx-auto font-light"
            >
              Senior project leadership for complex infrastructure, telecommunications, 
              and commercial developments across Australia.
            </motion.p>
            
            {/* CTA Buttons with Magnetic Effect */}
            <motion.div
              variants={itemVariants}
              className="flex flex-wrap justify-center gap-6"
            >
              <MagneticButton
                onClick={() => navigate('/portfolio')}
                className="btn-primary-custom px-12 py-8 text-xl group flex items-center gap-3"
              >
                View Portfolio
                <ArrowRight className="w-6 h-6 group-hover:translate-x-2 transition-transform" />
              </MagneticButton>
              
              <MagneticButton
                onClick={() => navigate('/contact')}
                className="btn-secondary-custom px-12 py-8 text-xl"
              >
                Start a Conversation
              </MagneticButton>
            </motion.div>
          </motion.div>
        </div>

        {/* Scroll Indicator */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 2, duration: 1, repeat: Infinity, repeatType: 'reverse' }}
          className="absolute bottom-12 left-1/2 transform -translate-x-1/2 z-20"
        >
          <div className="w-6 h-10 border-2 border-white border-opacity-30 rounded-full flex items-start justify-center p-2">
            <motion.div
              animate={{ y: [0, 12, 0] }}
              transition={{ duration: 1.5, repeat: Infinity }}
              className="w-1 h-3 bg-white bg-opacity-70 rounded-full"
            />
          </div>
        </motion.div>
      </section>

      {/* Impact by Numbers - Enhanced */}
      <section className="py-32 px-8 bg-navy-darker relative overflow-hidden">
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-5">
          <div className="absolute inset-0" style={{
            backgroundImage: 'radial-gradient(circle at 2px 2px, white 1px, transparent 0)',
            backgroundSize: '40px 40px'
          }} />
        </div>

        <div className="max-w-7xl mx-auto relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-100px' }}
            transition={{ duration: 0.8 }}
            className="text-center mb-20"
          >
            <div className="text-sm text-gray-500 uppercase tracking-[0.3em] mb-6 font-semibold">
              Impact by Numbers
            </div>
            <h2 className="text-5xl md:text-6xl font-bold text-white tracking-tight">
              Proven at Scale
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 lg:gap-16">
            {[
              { value: companyInfo.stats.projectValue, label: 'Project Value Delivered', prefix: '$', suffix: 'M+' },
              { value: companyInfo.stats.projectsDelivered, label: 'Projects Delivered', suffix: '+' },
              { value: companyInfo.stats.sectors, label: 'Major Sectors', suffix: '+' },
              { value: companyInfo.stats.yearsExperience, label: 'Years Leadership Experience', suffix: '+' }
            ].map((stat, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, scale: 0.8 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.2 }}
                whileHover={{ scale: 1.05 }}
                className="text-center group cursor-default"
              >
                <div className="relative inline-block max-w-full">
                  {/* Counter Container with Fixed Height */}
                  <div 
                    className="counter-display text-5xl md:text-6xl font-bold text-white mb-6 tracking-tight"
                    style={{
                      minHeight: '4.5rem',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      lineHeight: '1',
                      whiteSpace: 'nowrap'
                    }}
                  >
                    <AnimatedCounter
                      end={stat.value}
                      prefix={stat.prefix}
                      suffix={stat.suffix}
                      duration={2500}
                    />
                  </div>
                  {/* Underline */}
                  <motion.div
                    className="absolute -bottom-2 left-0 right-0 h-1 bg-white mx-auto"
                    style={{ maxWidth: '120px' }}
                    initial={{ scaleX: 0 }}
                    whileInView={{ scaleX: 1 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.8, delay: index * 0.2 + 0.5 }}
                  />
                </div>
                {/* Label */}
                <div className="text-sm text-gray-400 uppercase tracking-[0.2em] font-semibold mt-10 max-w-xs mx-auto leading-relaxed">
                  {stat.label}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* WOW: Split Screen Interactive Images */}
      <section className="py-24 px-8 bg-navy-darker">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
              Infrastructure Excellence
            </h2>
            <p className="text-lg text-gray-400">
              Delivering major projects across Australia
            </p>
          </motion.div>
          
          <SplitScreenImages
            leftImage={{
              url: "https://images.unsplash.com/photo-1759318965270-0549e993a3b8?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NzR8MHwxfHNlYXJjaHwxfHxpbmZyYXN0cnVjdHVyZSUyMGNvbnN0cnVjdGlvbiUyMHByb2plY3QlMjBhZXJpYWx8ZW58MHx8fHwxNzczMDQzMzk4fDA&ixlib=rb-4.1.0&q=85",
              alt: "Infrastructure construction",
              caption: "Major Infrastructure"
            }}
            rightImage={{
              url: "https://images.unsplash.com/photo-1772617661549-98d2ae35ab2d?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzNzl8MHwxfHNlYXJjaHwxfHxjb21tZXJjaWFsJTIwYnVpbGRpbmclMjBjb25zdHJ1Y3Rpb24lMjBzaXRlfGVufDB8fHx8MTc3MzA0MzM5OXww&ixlib=rb-4.1.0&q=85",
              alt: "Commercial development",
              caption: "Commercial Projects"
            }}
          />
        </div>
      </section>

      {/* Parallax Storytelling Section */}
      <ParallaxSection />

      {/* Sectors Section - Enhanced */}
      <section className="py-32 px-8 bg-navy-dark">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="text-center mb-20"
          >
            <div className="text-sm text-gray-500 uppercase tracking-[0.3em] mb-6 font-semibold">
              Industry Expertise
            </div>
            <h2 className="text-5xl md:text-6xl font-bold mb-8 tracking-tight text-white">
              Sectors We Serve
            </h2>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto font-light leading-relaxed">
              Delivering strategic project leadership across Australia's major infrastructure and commercial sectors
            </p>
          </motion.div>
          
          <SectorTiles sectors={sectors} />
        </div>
      </section>

      {/* Why Apex Build Project Management Section */}
      <section className="py-32 px-8 bg-navy-dark">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="text-center mb-20"
          >
            <div className="text-sm text-gray-500 uppercase tracking-[0.3em] mb-6 font-semibold">
              Why Choose Us
            </div>
            <h2 className="text-5xl md:text-6xl font-bold mb-8 tracking-tight text-white">
              Why Apex Build Project Management
            </h2>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto font-light leading-relaxed">
              Independent advisory delivering senior-level leadership and structured governance for complex projects
            </p>
          </motion.div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {whyApexReasons.map((reason, index) => {
              const IconComponent = reason.icon === 'shield' ? Shield : 
                                    reason.icon === 'award' ? Award :
                                    reason.icon === 'check-circle' ? CheckCircle2 :
                                    reason.icon === 'users' ? Users : Building2;
              
              return (
                <motion.div
                  key={reason.id}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  className="bg-navy-card border border-navy-border p-8 rounded-sm hover-lift-enhanced group cursor-default"
                >
                  <div className="w-14 h-14 bg-white bg-opacity-5 rounded-sm flex items-center justify-center mb-6 group-hover:bg-opacity-10 group-hover:scale-110 transition-all duration-300">
                    <IconComponent className="w-7 h-7 text-white" strokeWidth={1.5} />
                  </div>
                  <h3 className="text-2xl font-bold mb-4 text-white group-hover:text-gray-100 transition-colors">
                    {reason.title}
                  </h3>
                  <p className="text-gray-400 leading-relaxed group-hover:text-gray-300 transition-colors">
                    {reason.description}
                  </p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Storytelling Statement */}
      <section className="py-32 px-8 bg-navy-darker relative overflow-hidden">
        <motion.div
          initial={{ opacity: 0, scale: 1.2 }}
          whileInView={{ opacity: 0.05, scale: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 2 }}
          className="absolute inset-0 bg-gradient-to-br from-white to-transparent"
        />
        <div className="max-w-5xl mx-auto text-center relative z-10">
          <motion.h2
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="text-6xl md:text-7xl font-bold text-white leading-tight tracking-tight mb-8"
          >
            Complex projects demand<br />disciplined leadership.
          </motion.h2>
          <motion.div
            initial={{ width: 0 }}
            whileInView={{ width: '200px' }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.3 }}
            className="h-1 bg-white mx-auto"
          />
        </div>
      </section>

      {/* How We Deliver - Expandable Framework */}
      <section className="py-32 px-8 bg-navy-dark">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="text-center mb-20"
          >
            <div className="text-sm text-gray-500 uppercase tracking-[0.3em] mb-6 font-semibold">
              Our Framework
            </div>
            <h2 className="text-5xl md:text-6xl font-bold mb-8 tracking-tight text-white">
              How We Deliver
            </h2>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto font-light leading-relaxed">
              A structured, proven methodology providing clarity and control throughout project lifecycle
            </p>
          </motion.div>
          
          <ExpandableFramework framework={deliveryFramework} />
        </div>
      </section>

      {/* Storytelling Statement */}
      <section className="py-32 px-8 bg-navy-darker relative overflow-hidden">
        <div className="max-w-5xl mx-auto text-center relative z-10">
          <motion.h2
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="text-6xl md:text-7xl font-bold text-white leading-tight tracking-tight mb-8"
          >
            Structure creates certainty.
          </motion.h2>
          <motion.div
            initial={{ width: 0 }}
            whileInView={{ width: '200px' }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.3 }}
            className="h-1 bg-white mx-auto"
          />
        </div>
      </section>

      {/* WOW: Floating Image Gallery - Project Showcase */}
      <section className="py-32 px-8 bg-navy-dark">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="text-center mb-20"
          >
            <div className="text-sm text-gray-500 uppercase tracking-[0.3em] mb-6 font-semibold">
              Our Impact
            </div>
            <h2 className="text-5xl md:text-6xl font-bold text-white mb-6">
              Building Australia's Future
            </h2>
            <p className="text-xl text-gray-300 max-w-2xl mx-auto">
              Strategic leadership across telecommunications, infrastructure, and commercial sectors
            </p>
          </motion.div>
          
          <FloatingImageGallery
            images={[
              {
                url: "https://images.unsplash.com/photo-1770039998244-7ed06c2c9259?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NzR8MHwxfHNlYXJjaHwyfHxpbmZyYXN0cnVjdHVyZSUyMGNvbnN0cnVjdGlvbiUyMHByb2plY3QlMjBhZXJpYWx8ZW58MHx8fHwxNzczMDQzMzk4fDA&ixlib=rb-4.1.0&q=85",
                alt: "Stadium construction",
                caption: "Telecommunications",
                description: "Major stadium infrastructure"
              },
              {
                url: "https://images.unsplash.com/photo-1759931373726-298a1df1960c?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzNzl8MHwxfHNlYXJjaHwzfHxjb21tZXJjaWFsJTIwYnVpbGRpbmclMjBjb25zdHJ1Y3Rpb24lMjBzaXRlfGVufDB8fHx8MTc3MzA0MzM5OXww&ixlib=rb-4.1.0&q=85",
                alt: "Construction crane",
                caption: "Infrastructure",
                description: "Large-scale developments"
              },
              {
                url: "https://images.unsplash.com/photo-1768719951887-539ce3752a05?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzNzl8MHwxfHNlYXJjaHwyfHxjb21tZXJjaWFsJTIwYnVpbGRpbmclMjBjb25zdHJ1Y3Rpb24lMjBzaXRlfGVufDB8fHx8MTc3MzA0MzM5OXww&ixlib=rb-4.1.0&q=85",
                alt: "Commercial building",
                caption: "Commercial",
                description: "Premium developments"
              }
            ]}
          />
        </div>
      </section>

      {/* Refined CTA Section - "Discuss Confidentially" */}
      <section className="py-32 px-8 bg-navy-dark">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="text-center bg-white bg-opacity-5 backdrop-blur-sm border border-white border-opacity-20 p-16 rounded-sm"
          >
            <h2 className="text-5xl font-bold text-white mb-6 tracking-tight">
              Discuss Your Project Confidentially
            </h2>
            <p className="text-xl text-gray-300 mb-12 leading-relaxed font-light">
              All project discussions treated with complete confidentiality.<br />
              Connect to explore how we can support your delivery.
            </p>
            <MagneticButton
              onClick={() => navigate('/contact')}
              className="btn-primary-custom px-14 py-8 text-xl group inline-flex items-center gap-4"
            >
              Schedule a Conversation
              <ArrowRight className="w-6 h-6 group-hover:translate-x-2 transition-transform" />
            </MagneticButton>
          </motion.div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="py-32 px-8 bg-navy-dark relative overflow-hidden">
        {/* Background Gradient */}
        <motion.div
          animate={{
            scale: [1, 1.2, 1],
            rotate: [0, 90, 0]
          }}
          transition={{
            duration: 20,
            repeat: Infinity,
            ease: 'linear'
          }}
          className="absolute top-0 right-0 w-96 h-96 bg-white opacity-5 rounded-full blur-3xl"
        />

        <div className="max-w-4xl mx-auto text-center relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <h2 className="text-5xl md:text-6xl font-bold mb-8 text-white tracking-tight leading-tight">
              Ready to discuss<br />your next major project?
            </h2>
            <p className="text-xl text-gray-300 mb-16 leading-relaxed font-light">
              Connect with our senior leadership team to explore how we can support successful delivery.
            </p>
            <MagneticButton
              onClick={() => navigate('/contact')}
              className="btn-primary-custom px-16 py-10 text-2xl group inline-flex items-center gap-4"
            >
              Get in Touch
              <ArrowRight className="w-7 h-7 group-hover:translate-x-2 transition-transform" />
            </MagneticButton>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default FlagshipHomePage;
